import { getRequiredFields, removeEmptyFields, setEmptyFieldsToNull } from "@/lib/utils";
import { setPropertyData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useAddPropertyMutation, useGetActiveInsuranceTypes, useGetActivePropertyCategories, useGetActivePropertyModels, useGetActivePropertyZones, useGetPropertyBlockByCommunityZone, useGetPropertyById, useUpdatePropertyMutation } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { propertySchema } from "./validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldValue, useForm, useWatch } from "react-hook-form";
import {
  ADDRESS_1,
  ADDRESS_2,
  ZIPCODE,
  STATE,
  CITY,
  CATEGORY,
  INSURANCE_TYPE,
  NO_OF_GARAGES,
  PIN,
  POC_EMAIL,
  POC_MOB_NO,
  POC_NAME,
  PROPERTY_ID,
  PROPERTY_MODEL,
  PROPERTY_SIZE,
  ADDRESS,
  UNIT_NUMBER,
  EMERGENCY_CONTACTS,
  PROPERTY_BLOCK,
  PROPERTY_ZONE,
  COMMUNITY_ID,
  COUNTRY,
  PROPERTY_NAME,
  COMMUNITY_NAME,
  DISPLAY_ID,
} from "./consts";
import { toast } from "@/components/Toast";

const useAddEditProperty = () => {
  const { id: commId, propertyId, action } = useParams();
  const navigate = useNavigate();
  const isView = action == "view";
  const isAdd = !propertyId;
  const isEdit = action == "edit";
  const dispatch = useDispatch();
  const lastInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const isMasterData = location?.pathname?.toLowerCase()?.includes("master-data");
  
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const id = isMasterData ? communityData?.communityId : commId;
  
  const form = useForm<any>({
    defaultValues: {
      [PROPERTY_ID]: propertyId,
      [DISPLAY_ID]: "",
      [PROPERTY_NAME]: "",
      [UNIT_NUMBER]: "",
      [PIN]: "",
      [INSURANCE_TYPE]: undefined,
      [CATEGORY]: undefined,
      [PROPERTY_ZONE]: undefined,
      [PROPERTY_BLOCK]: undefined,
      [PROPERTY_MODEL]: undefined,
      [PROPERTY_SIZE]: "",
      [NO_OF_GARAGES]: undefined,
      // [IS_RENTED]: false,
      [ADDRESS]: {
        [ADDRESS_1]: "",
        [ADDRESS_2]: undefined,
        [STATE]: "",
        [CITY]: "",
        [ZIPCODE]: "",
        [COUNTRY]: "USA",
      },
      [EMERGENCY_CONTACTS]: [
        {
          [POC_NAME]: "",
          [POC_MOB_NO]: "",
          [POC_EMAIL]: "",
        },
      ],
      [COMMUNITY_ID]: id?.toString(),
      [COMMUNITY_NAME]: communityData?.name || "",
    },
    resolver: zodResolver(propertySchema),
  });
  const [emergeCount, setEmergeCount] = useState<number>(1);
  // View page edits Hierarchy / Address / Emergency contacts in place
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [key, setKey] = useState<any>({
    insuranceType: Math.random() * 2,
    propertyBlock: Math.random() * 2,
    propertyCategories: Math.random() * 2,
    propertyModels: Math.random() * 2,
    propertyZones: Math.random() * 2,
    stateKey: Math.random() * 2
  });

  const requiredFields = getRequiredFields(propertySchema);

  const stateList = useSelector(
    (state: RootState) => state.currentData.stateData
  );

  const { data: propertyDetails, isLoading: isDetailLoading, refetch: refetchDetails } =
    useGetPropertyById({
      communityId: communityData?.communityId,
      property_id: propertyId || "",
      queryParams: {
        enabled: !!propertyId,
      },
    });

  const zoneId = useWatch({control: form.control, name: PROPERTY_ZONE});

  const { data: insuranceType, isLoading: isInsuranceLoading } =
    useGetActiveInsuranceTypes({communityId: communityData?.communityId});
  const { data: propertyBlock, isLoading: isBlockLoading } =
    useGetPropertyBlockByCommunityZone({
      communityId: communityData?.communityId,
      zoneId: zoneId,
      queryParams: {
        enabled: !!zoneId
      }
    });
  const { data: propertyCategories, isLoading: isCategoryLoading } =
    useGetActivePropertyCategories({communityId: communityData?.communityId});
  const { data: propertyModels, isLoading: isModelLoading } =
    useGetActivePropertyModels({communityId: communityData?.communityId});
  const { data: propertyZones, isLoading: isZoneLoading } =
    useGetActivePropertyZones({communityId: communityData?.communityId});  

  const masterDataBreadcrumbData = [
    {
      link: ``,
      name: `Master Data`,
    },
    {
      link: ``,
      name: `Property Config`,
    },
    {
      link: `/${routes.admin}/${routes.masterData}/${routes.properties}`,
      name: "Manage Property",
    },
    {
      link: "",
      name: (
        <span className="text-primary">
          {isView
            ? `View Property - ${propertyDetails?.data?.name || ""}`
            : isEdit
            ? `Edit ${propertyDetails?.data?.name || ""} Details`
            : "Add Property"}
        </span>
      ),
    },
  ];

  const communityBreadcrumbData = [
    {
      link: `/${routes.admin}/${routes.adminConsole}/${routes.communities}`,
      name: `Community - ${communityData?.name}`,
    },
    {
      link: `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${id}/${routes.properties}`,
      name: "Manage Property",
    },
    {
      link: "",
      name: (
        <span className="text-primary">
          {isView
            ? `View Property - ${propertyDetails?.data?.name || ""}`
            : isEdit
            ? `Edit ${propertyDetails?.data?.name || ""} Details`
            : "Add Property"}
        </span>
      ),
    },
  ];

  const breadcrumbData = isMasterData ? masterDataBreadcrumbData : communityBreadcrumbData;

  const resetFromDetails = () => {
    if (!isAdd && propertyDetails) {
      dispatch(setPropertyData(propertyDetails?.data));
      const details = propertyDetails?.data;
      form.reset({
        [PROPERTY_ID]: propertyId?.toString(),
        [DISPLAY_ID]: details?.[DISPLAY_ID]?.toString() || "",
        [PROPERTY_NAME]: details?.[PROPERTY_NAME] || "",
        [UNIT_NUMBER]: details?.[UNIT_NUMBER]?.toString() || "",
        [PIN]: details?.[PIN]?.toString() || "",
        [INSURANCE_TYPE]: details?.[INSURANCE_TYPE]?.toString() || undefined,
        [CATEGORY]: details?.[CATEGORY]?.toString() || undefined,
        [PROPERTY_ZONE]: details?.[PROPERTY_ZONE]?.toString() || undefined,
        [PROPERTY_BLOCK]: details?.[PROPERTY_BLOCK]?.toString() || undefined,
        [PROPERTY_MODEL]: details?.[PROPERTY_MODEL]?.toString() || undefined,
        [PROPERTY_SIZE]: details?.[PROPERTY_SIZE]?.toString() || "",
        [NO_OF_GARAGES]: details?.[NO_OF_GARAGES]?.toString() || undefined,
        // [IS_RENTED]: details?.[IS_RENTED] || false,
        [ADDRESS]: {
          [ADDRESS_1]: details?.[ADDRESS]?.[ADDRESS_1] || "",
          [ADDRESS_2]: details?.[ADDRESS]?.[ADDRESS_2] || undefined,
          [STATE]: stateList?.find((data: any) => data?.name?.toLowerCase() == details?.[ADDRESS]?.[STATE]?.toLowerCase())?.code || "",
          [CITY]: details?.[ADDRESS]?.[CITY]?.toString() || "",
          [ZIPCODE]: details?.[ADDRESS]?.[ZIPCODE]?.toString() || "",
          [COUNTRY]: details?.[ADDRESS]?.[COUNTRY]?.toString() || "USA",
        },
        [EMERGENCY_CONTACTS]: details?.[EMERGENCY_CONTACTS] || [
          {
            [POC_NAME]: "",
            [POC_MOB_NO]: "",
            [POC_EMAIL]: "",
          },
        ],
        [COMMUNITY_ID]: id?.toString(),
        [COMMUNITY_NAME]: communityData?.name || ""
      })
      setEmergeCount(details?.[EMERGENCY_CONTACTS]?.length || 1);
      setKey({
        insuranceType: Math.random() * 2,
        propertyBlock: Math.random() * 2,
        propertyCategories: Math.random() * 2,
        propertyModels: Math.random() * 2,
        propertyZones: Math.random() * 2,
        stateKey: Math.random() * 2,
      });
    }
  };
  useEffect(resetFromDetails, [propertyDetails, isAdd]);

  const onBackClick = () => {
    const path = isMasterData ? `/${routes.admin}/${routes.masterData}/${routes.properties}` : `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${id}/${routes.properties}`
    navigate(path);
  };

  useEffect(() => {
    if (insuranceType) {
      setKey((prev: any) => ({ ...prev, insuranceType: Math.random() * 4 }));
    }
  }, [insuranceType]);

  useEffect(() => {
    if (propertyBlock) {
      setKey((prev: any) => ({ ...prev, propertyBlock: Math.random() * 4 }));
    }
  }, [propertyBlock]);

  useEffect(() => {
    if (propertyCategories) {
      setKey((prev: any) => ({
        ...prev,
        propertyCategories: Math.random() * 4,
      }));
    }
  }, [propertyCategories]);

  useEffect(() => {
    if (propertyModels) {
      setKey((prev: any) => ({ ...prev, propertyModels: Math.random() * 4 }));
    }
  }, [propertyModels]);
  useEffect(() => {
    if (propertyZones) {
      setKey((prev: any) => ({ ...prev, propertyZones: Math.random() * 4 }));
    }
  }, [propertyZones]);

  const { mutate: addInfo, isPending: isAddPending } = useAddPropertyMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      onBackClick()
      toast.success("Property Info Added Successfully");
    },
  });

  // update property
  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdatePropertyMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        if (isView) {
          setIsInlineEditing(false);
          refetchDetails();
        } else {
          onBackClick();
        }
        toast.success("Property Info Updated Successfully");
      },
    });

  const onSubmit = (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    let hasError = false;

    // if (_data[EMERGENCY_CONTACTS]) {
    //   if (
    //     !(_data[EMERGENCY_CONTACTS] as any)?.[0]?.[POC_NAME] ||
    //     !(_data[EMERGENCY_CONTACTS] as any)?.[0]?.[POC_MOB_NO] ||
    //     !(_data[EMERGENCY_CONTACTS] as any)?.[0]?.[POC_EMAIL]
    //   ) {
    //     form.setError(EMERGENCY_CONTACTS, {
    //       message:
    //         "Please add at least one emergency contact with name, mobile number and email",
    //     });
    //     return;
    //   }
    // }

    (_data[EMERGENCY_CONTACTS] as any)?.forEach(
      (contact: any, index: number) => {
        const name = contact[POC_NAME];
        const mob = contact[POC_MOB_NO];
        const email = contact[POC_EMAIL];

        if (name && !mob && !email) {
          // Set error on both fields to show message
          form.setError(`${EMERGENCY_CONTACTS}.${index}.${POC_MOB_NO}`, {
            type: "manual",
            message: "Mobile number if name is filled",
          });
          form.setError(`${EMERGENCY_CONTACTS}.${index}.${POC_EMAIL}`, {
            type: "manual",
            message: "Email is required if name is filled",
          });
          hasError = true;
        }
      }
    );

    if (hasError) return;

    _data[EMERGENCY_CONTACTS] = (_data[EMERGENCY_CONTACTS] as any)
      ?.filter((val: any) => val?.[POC_NAME]) // Remove entries with no name
      .map((val: any) => ({
        [POC_NAME]: val?.[POC_NAME] === "" ? null : val[POC_NAME],
        [POC_MOB_NO]: val?.[POC_MOB_NO] === "" ? null : val[POC_MOB_NO],
        [POC_EMAIL]: val?.[POC_EMAIL] === "" ? null : val[POC_EMAIL],
      }));

    const address = {
      ...data.address,
      [STATE]: stateList.find(
        (state: any) => state.code == (_data[ADDRESS] as any)[STATE]
      )?.name,
    };
    if (propertyId) {
      delete _data[PROPERTY_ID];
      updateInfo({
        communityId: communityData?.communityId,
        id: propertyId,
        data: {
          ..._data,
          [ADDRESS]: address,
        },
      });
    } else {
      addInfo({
        data: {
          ..._data,
          [ADDRESS]: address,
        },
      });
    }
  };

  const onAddMore = () => {
    setEmergeCount(emergeCount + 1);
    setTimeout(() => {
      if (lastInputRef.current) {
        lastInputRef.current.focus();
      }
    }, 100);
  };

  const onRemove = (index: number) => {
    setEmergeCount(emergeCount - 1);

    form.setValue(
      EMERGENCY_CONTACTS,
      form
        .getValues(EMERGENCY_CONTACTS)
        .filter((_: any, i: number) => i !== index)
    );
  };
  
  const propertyZoneValue = useWatch({control: form.control, name: PROPERTY_ZONE});
  const propertyBlockValue = useWatch({control: form.control, name: PROPERTY_BLOCK});

  const zoneName = useMemo(() => propertyZones?.data?.find((data: any) => data?.zoneId?.toString() == propertyZoneValue?.toString())?.zoneName || "", [propertyZoneValue, propertyZones, propertyDetails]);
  const blockName = useMemo(() => propertyBlock?.data?.find((data: any) => data?.blockId?.toString() == propertyBlockValue?.toString())?.blockName || "", [propertyBlockValue, propertyBlock, propertyDetails]);


  const displayHierarchy = useMemo(() => {
    return zoneName && blockName ? `${communityData?.name} ${zoneName ? ` - ${zoneName}` : ''} ${blockName ? `- ${blockName}` : ''}` : '-'
  }, [zoneName, blockName]);

  const generatedId = useMemo(() => {
    const id = zoneName && blockName ? `${communityData?.name} ${zoneName ? ` - ${zoneName}` : ''} ${blockName ? `- ${blockName}` : ''} ${propertyDetails?.data?.displayId ? `- ${propertyDetails?.data?.displayId}` : ''}` : '-';
    // form.setValue(DISPLAY_ID, id);
    return id
  }, [zoneName, blockName]);

  return {
    onBackClick,
    isView,
    propertyId,
    isDetailLoading,
    isAdd,
    breadcrumbData,
    form,
    onSubmit,
    requiredFields,
    emergeCount,
    setEmergeCount,
    onAddMore,
    isAddPending,
    isUpdatePending,
    stateList,
    onRemove,
    lastInputRef,
    insuranceType,
    propertyBlock,
    propertyCategories,
    propertyModels,
    propertyZones,
    isInsuranceLoading,
    isBlockLoading,
    isCategoryLoading,
    isModelLoading,
    isZoneLoading,
    key,
    displayHierarchy,
    zoneName,
    blockName,
    onCancel: onBackClick,
    propertyDetails: propertyDetails?.data,
    isInlineEditing,
    setIsInlineEditing,
    resetFromDetails,
    generatedId,
  };
};

export default useAddEditProperty;
